import { useCallback, useContext } from "react";
import * as Sentry from "@sentry/react";
import { UserContext } from "../contexts/UserContext";
import { SyllabusContext } from "../contexts/SyllabusContext";
import { useSubjects } from "../contexts/SubjectsContext";
import { useClearChat } from "../contexts/ClearChatContext";
import { useSidebar } from "../contexts/SidebarContext";
import { useLeaderboard } from "../contexts/LeaderboardContext";
import { useQuiz } from "../contexts/QuizContext";
import { useTeachingMode } from "../contexts/TeachingModeContext";
import StorageService from "../services/storageService";
import { clearAllBrowserCaches } from "../utils/cacheUtils";
import { clearStoredLearningSubject } from "../utils/learningSubjectStorage";

const STORAGE_KEYS_TO_REMOVE = [
  "token",
  "userId",
  "userToken",
  "profileUpdated",
  "rememberMe",
  "userPincode",
  "workbookGuideShown",
  "onboardingChecklist",
  "onboardingComplete",
];

export const useLogoutCleanup = () => {
  const { resetUserData } = useContext(UserContext);
  const syllabusContext = useContext(SyllabusContext);
  const { resetSyllabusState } = syllabusContext || {};
  const subjectsContext = useSubjects() || {};
  const { resetSubjectsState } = subjectsContext || {};
  const clearChatContext = useClearChat();
  const { resetClearChatState } = clearChatContext || {};
  const sidebarContext = useSidebar();
  const { resetSidebarState } = sidebarContext || {};
  const leaderboardContext = useLeaderboard() || {};
  const { resetLeaderboardState } = leaderboardContext || {};
  const quizContext = useQuiz() || {};
  const { resetQuizState } = quizContext || {};
  const teachingModeContext = useTeachingMode();
  const { resetToDefaultMode } = teachingModeContext || {};

  const resetRuntimeContexts = useCallback(() => {
    if (typeof window !== "undefined") {
      window.goalsData = null;
    }
    Sentry.setUser(null);
    resetUserData?.();
    resetSyllabusState?.();
    resetSubjectsState?.();
    resetClearChatState?.();
    resetSidebarState?.();
    resetLeaderboardState?.();
    resetQuizState?.();
    resetToDefaultMode?.();
  }, [
    resetClearChatState,
    resetLeaderboardState,
    resetQuizState,
    resetSidebarState,
    resetSubjectsState,
    resetSyllabusState,
    resetToDefaultMode,
    resetUserData,
  ]);

  const clearPersistentStorage = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }
    clearStoredLearningSubject();
    STORAGE_KEYS_TO_REMOVE.forEach((key) => StorageService.removeItem(key));
    window.localStorage?.clear();
    window.sessionStorage?.clear();
    window.localStorage?.setItem("JOURNEY_MODAL_FLAG", "true");
  }, []);

  const performLogoutCleanup = useCallback(
    async ({ redirectPath = "/login", hardRefresh = false } = {}) => {
      clearPersistentStorage();
      resetRuntimeContexts();

      try {
        await clearAllBrowserCaches();
      } catch (cacheError) {
        console.error("Failed to fully clear browser caches", cacheError);
      }

      if (hardRefresh && typeof window !== "undefined") {
        window.location.replace(redirectPath);
        return;
      }

      return redirectPath;
    },
    [clearPersistentStorage, resetRuntimeContexts],
  );

  return { performLogoutCleanup };
};
